import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import CustomModal from "../../../../components/modal";
import Spinner from "../../../../components/Loaders/Spinner";
import ButtonIcon from "../../../../components/ButtonIcon/ButtonIcon";
import Button from "../../../../components/new-button";
import { TrackJS } from "trackjs";
import {
  createArrow,
  createSignatureButton,
  createSignatureImg,
  createStartButton,
  updateButton,
  updateSignatureImgSize,
  updateTimeStamp,
  getPdfUrl,
} from "../../../../../components/helper/helper";
import Axios from "axios";
import adminSignature from "../../../../../../images/v2/adminsignature.png";
import momentTimezone from "moment-timezone";
import { uploadPdf } from "../../../../../api";
import useSalesFormStore from "../../../../stores/sales-form";
import "./style.scss";

const ViewPdfModal = ({
  handleViewedPDF,
  pdf,
  setPdf,
  viewOnly,
  signature,
  editSlug,
  signedName,
  signerType,
  setViewedPdfIndex,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const otpCode = useSalesFormStore((state) => state.otpCode);
  const [isEnd, setIsEnd] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [scale, setScale] = useState(null);
  const [signedCoordinates, setSignedCoordinates] = useState([]);
  const [modalReady, setModalReady] = useState(false); //to check if the modal is ready to be used
  const [signingStarted, setSigningStarted] = useState(false);
  const [currentSignIndex, setCurrentSignIndex] = useState(null);
  const [flatSignCoordinates, setFlatSignCoordinates] = useState([]);
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const resizeTimeoutRef = useRef(null);

  useEffect(() => {
    if (!pdf?.url || !canvasRef.current) {
      return;
    }

    const handleResize = () => {
      clearTimeout(resizeTimeoutRef.current);

      resizeTimeoutRef.current = setTimeout(() => {
        if (!canvasRef.current) return;
        const width = canvasRef.current.offsetWidth;
        if (!width) return;

        const nextScale = (width - 16) / 600;

        setScale((prev) => {
          if (!prev) return nextScale;
          if (Math.abs(prev - nextScale) < 0.01) return prev;
          return nextScale;
        });
      }, 100);
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(resizeTimeoutRef.current);
      window.removeEventListener("resize", handleResize);
    };
  }, [pdf, modalReady]);

  const calculateScale = () => {
    const screenWidth = Number(window.innerWidth);
    let viewportScale = 1.4;
    switch (true) {
      case screenWidth > 900: {
        viewportScale = 1.4;
        break;
      }
      case screenWidth < 900 && screenWidth > 800: {
        viewportScale = 1.2;
        break;
      }
      case screenWidth < 800 && screenWidth > 700: {
        viewportScale = 1.1;
        break;
      }
      case screenWidth < 700 && screenWidth > 500: {
        viewportScale = 0.8;
        break;
      }
      case screenWidth < 500 && screenWidth > 400: {
        viewportScale = 0.55;
        break;
      }
      case screenWidth < 400: {
        viewportScale = 0.5;
        break;
      }
      default: {
        viewportScale = 1.4;
        break;
      }
    }
    return viewportScale;
  };

  const createPDFService = () => {
    try {
      const signaturePositions = [];
      const adminSignaturePositions = [];
      if (pdf?.pdf_sign_coordinate?.length > 0) {
        const reorderedSignatureCoordinates = reorderCordinate(
          pdf?.pdf_sign_coordinate,
        );
        for (
          let pageId = 0;
          pageId < reorderedSignatureCoordinates?.length;
          pageId++
        ) {
          const pageCoordinates = reorderedSignatureCoordinates[pageId].map(
            (coordinate) => {
              const [x, y] = coordinate
                .replace(/"/g, "")
                .split(",")
                .map((coord) => parseInt(coord, 10));
              return { x, y };
            },
          );
          signaturePositions.push(
            ...pageCoordinates.map(({ x, y }) => ({
              pageId: pageId,
              x,
              y,
            })),
          );
        }
      }
      if (pdf?.admin_pdf_sign_coordinate?.length > 0) {
        const reorderedAdminSignatureCoordinates = reorderCordinate(
          pdf?.admin_pdf_sign_coordinate,
        );
        for (
          let pageId = 0;
          pageId < reorderedAdminSignatureCoordinates?.length;
          pageId++
        ) {
          const pageCoordinates = reorderedAdminSignatureCoordinates[
            pageId
          ].map((coordinate) => {
            const [x, y] = coordinate
              .replace(/"/g, "")
              .split(",")
              .map((coord) => parseInt(coord, 10));
            return { x, y };
          });
          adminSignaturePositions.push(
            ...pageCoordinates.map(({ x, y }) => ({ pageId: pageId, x, y })),
          );
        }
      }
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const timezoneAbbreviation = momentTimezone
        .tz(momentTimezone.tz.guess())
        .format("z");
      return Axios.get(pdf.url, { responseType: "blob" }).then((res) => {
        const form = new FormData();
        form.append("pdf", pdf?.url);
        form.append("signature", signature);
        // form.append("signatureName", signedName);
        form.append("signaturePositions", JSON.stringify(signaturePositions));
        form.append(
          "adminSignaturePositions",
          JSON.stringify(adminSignaturePositions),
        );
        form.append("editSlug", editSlug);
        form.append("pdfName", pdf?.name);
        form.append("signerType", signerType);
        form.append("userTimezone", userTimezone);
        form.append("timezoneAbbreviation", timezoneAbbreviation);

        return uploadPdf(editSlug, form, { headers: { OtpCode: otpCode } })
          .then((response) => {
            return response?.status;
          })
          .catch((err) => {
            console.log(err);
          });
      });
    } catch (err) {
      console.log("error in createPDFService", err);
    }
  };

  const handleScroll = () => {
    const wrapper = wrapperRef.current;
    if (wrapper) {
      if (
        wrapper.scrollTop + wrapper.clientHeight >=
        wrapper.scrollHeight - 200
      ) {
        setIsEnd(true);
      }
    }
  };

  useEffect(() => {
    setIsEnd(false);
  }, [pdf]);

  const buttons = [];
  const arrows = [];
  const images = [];
  const timestamp = [];

  useEffect(() => {
    // NOTE: THIS EFFECT HAS A LOT OF UNCLEARED
    // EVENT REFERENCES AND OVERALL NEEDS TO BE CLEANED
    // UP AND REFACTORED.
    if (pdf?.url && scale) {
      try {
        setLoading(true);
        const signed = [...signedCoordinates];
        canvasRef.current.innerHTML = "";
        const loadingTask = window.pdfjsLib.getDocument(
          Boolean(pdf?.submittedOn) && pdf?.isESigned
            ? getPdfUrl(pdf?.url, signerType)
            : pdf?.url,
        );
        const container = document.getElementById("canvas");
        const emptySignCords = {};
        const signatureCoords = {};
        const adminSignatureCoords = {};
        loadingTask.promise.then(async (pdfDocument) => {
          if (!areArraysEmpty(pdf?.pdf_sign_coordinate)) {
            for (let i = 0; i < pdf?.pdf_sign_coordinate?.length; i++) {
              signatureCoords[(i + 1).toString()] = pdf?.pdf_sign_coordinate[i];
            }
          } else {
            setIsAllChecked(true);
            for (let i = 1; i <= pdfDocument.numPages; i++) {
              emptySignCords[i] = [];
            }
          }
          if (
            pdf?.admin_pdf_sign_coordinate &&
            pdf?.admin_pdf_sign_coordinate?.length > 0
          ) {
            for (let i = 0; i < pdf?.admin_pdf_sign_coordinate?.length; i++) {
              adminSignatureCoords[(i + 1).toString()] =
                pdf?.admin_pdf_sign_coordinate[i];
            }
          }
          const signatureCoordinates = pdf?.pdf_sign_coordinate
            ? signatureCoords
            : emptySignCords;
          const adminSignatureCoordinates = adminSignatureCoords;
          const mainDebugData = {
            pdfName: pdf?.name,
            editSlug: editSlug,
            pdfSignCoordinates: pdf?.pdf_sign_coordinate,
            arePdfSignCoordinatesEmpty: areArraysEmpty(
              pdf?.pdf_sign_coordinate,
            ),
            submittedOn: pdf?.submittedOn,
            isESigned: pdf?.isESigned,
          };
          if (
            !areArraysEmpty(pdf?.pdf_sign_coordinate) &&
            !Boolean(pdf?.submittedOn) &&
            !pdf?.isESigned
          ) {
            const startButton = createStartButton();
            startButton.classList.add("pdf-start-sign-button");
            if (container) {
              if (signed?.length > 0) {
                console.log({ signed, ...mainDebugData });
                TrackJS &&
                  TrackJS.track(
                    "PDFSignStartButton:skipped:signedLengthGreaterThanZero",
                  );
                startButton.style.display = "none";
              }
              startButton.addEventListener("click", () => {
                const firstPageIndex =
                  findFirstNonEmptyArray(signatureCoordinates);
                const firstSignPageCoordinates =
                  signatureCoordinates[`${firstPageIndex}`];
                const nextCoordinate =
                  firstSignPageCoordinates[firstSignPageCoordinates.length - 1];
                const [X, Y] = nextCoordinate.split(",").map(Number);
                const nextButton = document.getElementById(
                  `signButton-${X}-${Y}-${firstPageIndex}`,
                );
                const nextArrow = document.getElementById(
                  `arrow-${X}-${Y}-${firstPageIndex}`,
                );
                startButton.style.display = "none";
                if (nextButton) {
                  nextButton.style.visibility = "visible";
                  nextArrow.style.visibility = "visible";
                  nextButton.scrollIntoView({
                    block: "center",
                    behavior: "smooth",
                  });
                }
              });
              console.log(mainDebugData);
              TrackJS &&
                TrackJS.track("PDFSignStartButton:success:beforeButtonAppend");
              container.appendChild(startButton);
            }
          } else {
            console.log(mainDebugData);
            TrackJS &&
              TrackJS.track(
                "PDFSignStartButton:failed:conditionNotMet",
                mainDebugData,
              );
          }

          const reorderedSignatureCoordinates =
            reorderSignatureCoordinates(signatureCoordinates);
          const flat = [];
          for (const pageKey in reorderedSignatureCoordinates) {
            reorderedSignatureCoordinates[pageKey].forEach((coord) => {
              const [x, y] = coord.split(",").map(Number);
              flat.push({
                x,
                y,
                page: Number(pageKey),
                baseId: `${x}-${y}-${pageKey}`,
              });
            });
          }
          setFlatSignCoordinates(flat);

          // Iterate through the pages
          for (let pageNum = 1; pageNum <= pdfDocument.numPages; pageNum++) {
            const page = await pdfDocument.getPage(pageNum);
            const pageScale = scale;
            const viewport = page.getViewport({ scale: pageScale });
            const pageWidth = viewport.width;
            const pageHeight = viewport.height;
            const containerDiv = document.createElement("div");
            containerDiv.id = `pdfContainer${pageNum}`;
            containerDiv.style.position = "relative";
            const canvas = document.createElement("canvas");
            canvas.id = `pdfCanvas${pageNum}`;
            canvas.width = pageWidth;
            canvas.height = pageHeight;
            const context = canvas.getContext("2d");
            const renderContext = {
              canvasContext: context,
              viewport: viewport,
            };
            await page.render(renderContext).promise;
            const pageSignatureCoordinates =
              signatureCoordinates[pageNum.toString()];
            const pageAdminSignatureCoordinates =
              adminSignatureCoordinates[pageNum.toString()];
            if (
              pageAdminSignatureCoordinates &&
              !Boolean(pdf?.submittedOn) &&
              !pdf?.isESigned
            ) {
              pageAdminSignatureCoordinates.forEach(
                async (coordinate, index) => {
                  const [signX, signY] = coordinate.split(",").map(Number);
                  const adjustedSignX = signX * viewport.scale;
                  const adjustedSignY = pageHeight - signY * viewport.scale;
                  const adminSign = document.createElement("img");
                  adminSign.src = `${adminSignature}`;
                  adminSign.alt = "adminSign";
                  adminSign.style.position = "absolute";
                  adminSign.style.left = `${adjustedSignX}px`;
                  adminSign.style.top = `${adjustedSignY + 6}px`;
                  adminSign.style.maxWidth = "200px";
                  adminSign.style.maxHeight = "25px";
                  adminSign.style.display = "block";
                  await updateSignatureImgSize(
                    adminSign,
                    adjustedSignX,
                    adjustedSignY,
                  );
                  containerDiv.appendChild(adminSign);

                  const adminTimestampLabel = document.createElement("div");
                  adminTimestampLabel.textContent = getTimestamp();
                  adminTimestampLabel.style.position = "absolute";
                  adminTimestampLabel.style.display = "block";
                  adminTimestampLabel.style.fontSize = "10px";
                  adminTimestampLabel.style.lineHeight = "0.8";
                  adminTimestampLabel.style.left = `${adjustedSignX}px`;
                  adminTimestampLabel.style.top = `${adjustedSignY + 31}px`;
                  await updateTimeStamp(
                    adminTimestampLabel,
                    adjustedSignX,
                    adjustedSignY,
                  );
                  containerDiv.appendChild(adminTimestampLabel);
                },
              );
            }
            if (
              pageSignatureCoordinates &&
              !Boolean(pdf?.submittedOn) &&
              !pdf?.isESigned
            ) {
              pageSignatureCoordinates.forEach(async (coordinate) => {
                const [signX, signY] = coordinate.split(",").map(Number);
                const adjustedSignX = signX * viewport.scale;
                const adjustedSignY = pageHeight - signY * viewport.scale;
                if (pdf?.pdf_sign_coordinate) {
                  const baseId = `${signX}-${signY}-${pageNum}`;

                  // create a image element
                  const sign = createSignatureImg();
                  sign.id = `sign-img-${baseId}`;
                  sign.src = `${signature}`;
                  sign.alt = "sign" + pageNum;
                  sign.style.display = signed?.includes(`${baseId}-signed`)
                    ? "block"
                    : "none";
                  sign.style.left = `${adjustedSignX}px`;
                  sign.style.top = `${adjustedSignY + 6}px`;
                  const { maxWidth, maxHeight } = updateSignatureImgSize(
                    sign,
                    adjustedSignX,
                    adjustedSignY,
                  );
                  containerDiv.appendChild(sign);
                  images.push(sign);

                  const highlight = document.createElement("div");
                  highlight.id = `highlight-${baseId}`;
                  highlight.classList.add("pdf-sign-highlight");
                  highlight.style.position = "absolute";
                  highlight.style.left = `${adjustedSignX}px`;
                  highlight.style.top = `${adjustedSignY + 6}px`;
                  highlight.style.width = maxWidth;
                  highlight.style.height = maxHeight;
                  highlight.style.pointerEvents = "none";
                  highlight.style.zIndex = "97";
                  highlight.style.display = signed?.includes(`${baseId}-signed`)
                    ? "none"
                    : "block";

                  containerDiv.appendChild(highlight);

                  // Create a timestamp label
                  const timestampLabel = document.createElement("div");
                  timestampLabel.textContent = getTimestamp();
                  timestampLabel.id = `timestamp-${baseId}`;
                  timestampLabel.className = `pdf-timestamp`;
                  timestampLabel.style.position = "absolute";
                  timestampLabel.style.display = signed?.includes(
                    `${baseId}-signed`,
                  )
                    ? "block"
                    : "none";
                  timestampLabel.style.fontSize = "10px";
                  timestampLabel.style.lineHeight = "0.8";
                  timestampLabel.style.left = `${adjustedSignX}px`;
                  timestampLabel.style.top = `${adjustedSignY + 31}px`;
                  timestampLabel.style.width = `max-content`;
                  await updateTimeStamp(
                    timestampLabel,
                    adjustedSignX,
                    adjustedSignY,
                  );
                  timestamp.push(timestampLabel);
                  containerDiv.appendChild(timestampLabel);
                  //note: re-check if this is needed or something else can
                  //be used as an anchor for scrolling to the signature
                  const scrollAnchor = document.createElement("div");
                  scrollAnchor.id = `pdf-scroll-anchor-${baseId}`;
                  scrollAnchor.className = "pdf-scroll-anchor";
                  scrollAnchor.setAttribute("aria-hidden", "true");
                  scrollAnchor.style.position = "absolute";
                  scrollAnchor.style.left = `${adjustedSignX}px`;
                  scrollAnchor.style.top = `${adjustedSignY + 6}px`;
                  scrollAnchor.style.width = "4px";
                  scrollAnchor.style.height = "4px";
                  scrollAnchor.style.opacity = "0";
                  scrollAnchor.style.pointerEvents = "none";
                  scrollAnchor.style.zIndex = "98";
                  containerDiv.appendChild(scrollAnchor);

                  //Create a arrow Element
                  const arrow = createArrow();
                  arrow.className = `pdf-arrow`;
                  arrow.id = `arrow-${baseId}`;
                  arrow.style.top = `${adjustedSignY}px`;
                  arrow.style.visibility =
                    signed?.includes(`${baseId}-signed`) ||
                    signed?.includes(`${baseId}-removed`)
                      ? "visible"
                      : "hidden";
                  arrow.style.left = `${pageWidth - 7}px`;
                  containerDiv.appendChild(arrow);
                  arrows.push(arrow);

                  // Create a functional button
                  const button = createSignatureButton();
                  const buttonId = `signButton-${baseId}`;
                  button.className = `pdf-button`;
                  button.id = buttonId;
                  button.style.visibility =
                    signed?.includes(`${baseId}-signed`) ||
                    signed?.includes(`${baseId}-removed`)
                      ? "visible"
                      : "hidden";
                  button.innerText = signed?.includes(`${baseId}-signed`)
                    ? "Remove"
                    : "Sign";
                  button.style.top = `${adjustedSignY - 3}px`;
                  button.style.left = `${pageWidth}px`;
                  buttons.push(button);
                  updateButton(button, pageWidth);

                  button.addEventListener("click", async (event) => {
                    const nextSignCoordinates = findNextCoordinate(
                      coordinate,
                      reorderedSignatureCoordinates,
                      pageNum,
                    );
                    const button = event.target;
                    const buttonInnerText = button.innerText;
                    const arrow = document.getElementById(`arrow-${baseId}`);
                    let nextButton;
                    let nextArrow;
                    const img = document.getElementById(`sign-img-${baseId}`);
                    const timestamp = document.getElementById(
                      `timestamp-${baseId}`,
                    );

                    if (nextSignCoordinates !== "End") {
                      const [X, Y, Page] = nextSignCoordinates
                        .split(",")
                        .map(Number);
                      nextButton = document.getElementById(
                        `signButton-${X}-${Y}-${Page}`,
                      );
                      nextArrow = document.getElementById(
                        `arrow-${X}-${Y}-${Page}`,
                      );
                    }
                    if (buttonInnerText === "Sign") {
                      button.innerText = "Remove";
                      button.style.right = "7px";
                      button.style.padding = "0px 7px";
                      img.style.display = "block";
                      timestamp.style.display = "block";
                      if (!nextButton && nextSignCoordinates === "End") {
                        const wrapper = wrapperRef.current;
                        if (wrapper) {
                          wrapper.scrollTop = wrapper.scrollHeight;
                        }
                      } else if (nextButton) {
                        nextButton.style.visibility = "visible";
                        nextArrow.style.visibility = "visible";
                        nextButton.scrollIntoView({
                          block: "center",
                          behavior: "smooth",
                        });
                      }
                      signed.push(`${baseId}-signed`);
                      setSignedCoordinates(signed);
                    } else if (buttonInnerText === "Remove") {
                      button.innerText = "Sign" + "\u00A0";
                      button.style.right = "24px";
                      button.style.padding = "0px 10px";
                      img.style.display = "none";
                      timestamp.style.display = "none";
                      signed.splice(signed.indexOf(`${baseId}-signed`), 1);
                      signed.push(`${baseId}-removed`);
                      setSignedCoordinates(signed);
                    } else if (buttonInnerText === "Sign" + "\u00A0") {
                      button.innerText = "Remove";
                      button.style.right = "7px";
                      button.style.padding = "0px 7px";
                      img.style.display = "block";
                      timestamp.style.display = "block";
                      signed.push(`${baseId}-signed`);
                      setSignedCoordinates(signed);
                    }

                    const images = document.getElementsByClassName("sign-img");

                    const allVisible = Array.from(images).every(
                      (sign) => sign.style.display === "block",
                    );
                    setIsAllChecked(allVisible);
                  });

                  containerDiv.appendChild(button);
                }
              });
            }
            if (signed?.length > 0) {
              const lastSignCord = findGreatestPageSmallestY(signed);
              const nextSignCoordinates = findNextCoordinate(
                `${lastSignCord.x},${lastSignCord.y}`,
                reorderedSignatureCoordinates,
                lastSignCord.pageNum,
              );
              const [X, Y, Page] = nextSignCoordinates.split(",").map(Number);
              const nextButton = document.getElementById(
                `signButton-${X}-${Y}-${Page}`,
              );
              const nextArrow = document.getElementById(
                `arrow-${X}-${Y}-${Page}`,
              );
              if (nextButton) {
                nextButton.style.visibility = "visible";
                nextArrow.style.visibility = "visible";
                nextButton.scrollIntoView({
                  block: "center",
                  behavior: "smooth",
                });
              }
            }

            if (containerDiv) {
              containerDiv.appendChild(canvas);
            }
            if (container) {
              container.appendChild(containerDiv);
            }
          }
          setLoading(false);
        });
      } catch (err) {
        console.log("error 111", err);
        handleError();
      }
    }
  }, [pdf, scale]);

  const areArraysEmpty = (arr) => {
    for (let i = 0; i < arr.length; i++) {
      if (arr[i].length > 0) {
        return false;
      }
    }
    return true;
  };

  const reorderSignatureCoordinates = (coordinates) => {
    const reorderedCoordinatesData = {};
    for (const key in coordinates) {
      reorderedCoordinatesData[key] = coordinates[key].slice().reverse();
    }
    return reorderedCoordinatesData;
  };

  const reorderCordinate = (coordinates) => {
    const reorderedCoordinatesData = [];
    for (const key in coordinates) {
      reorderedCoordinatesData[key] = coordinates[key].slice().reverse();
    }
    return reorderedCoordinatesData;
  };

  const findNextCoordinate = (
    currentCoordinate,
    coordinatesData,
    currentpage,
  ) => {
    let found = false;
    for (const key in coordinatesData) {
      if (Number(key) >= currentpage) {
        const coordinates = coordinatesData[key];
        if (coordinates.length === 0) {
          continue;
        }
        if (coordinates.includes(currentCoordinate)) {
          found = true;
          const index = coordinates.indexOf(currentCoordinate);
          if (index < coordinates.length - 1) {
            const nextCoordinate = coordinates[index + 1];
            return nextCoordinate + "," + currentpage;
          } else if (found && key < Object.keys(coordinatesData).length) {
            const nextKey = String(Number(key) + 1);
            const nextCoordinates = coordinatesData[nextKey];
            if (nextCoordinates && nextCoordinates.length > 0) {
              const nextCoordinate = nextCoordinates[0];
              return nextCoordinate + "," + nextKey;
            } else if (nextCoordinates && nextCoordinates.length === 0) {
              return findNextNonEmptyCoordinate(nextKey, coordinatesData);
            } else {
              return "End";
            }
          }
          return "End";
        }
      }
    }
  };

  const findNextNonEmptyCoordinate = (startKey, coordinatesData) => {
    for (const key in coordinatesData) {
      if (Number(key) > Number(startKey)) {
        const nextCoordinates = coordinatesData[key];
        if (nextCoordinates && nextCoordinates.length > 0) {
          return nextCoordinates[0] + "," + key;
        }
      }
    }
    return "End";
  };

  const findFirstNonEmptyArray = (data) => {
    for (const key in data) {
      if (data[key].length > 0) {
        return key;
      }
    }
    return null;
  };

  const findGreatestPageSmallestY = (arr) => {
    let result = null;
    arr.forEach((item) => {
      const [x, y, pageNum] = item.split("-").map(Number);
      if (
        !result ||
        pageNum > result.pageNum ||
        (pageNum === result.pageNum && y < result.y)
      ) {
        result = { x, y, pageNum };
      }
    });
    return result;
  };

  const getTimestamp = () => {
    const now = new Date();
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneAbbreviation = momentTimezone
      .tz(momentTimezone.tz.guess())
      .format("z");
    const formattedTime = new Intl.DateTimeFormat("en-US", {
      timeZone: userTimezone,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).format(now);
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const timestamp = `${month}-${day}-${year} ${formattedTime} ${timezoneAbbreviation}`;
    return timestamp;
  };

  const onPdfClose = async () => {
    setIsSubmitting(true);
    const signedPdfStatus = await createPDFService();
    if (signedPdfStatus === true) {
      handleViewedPDF(pdf, signedPdfStatus);
      setViewedPdfIndex((prevIndex) => prevIndex + 1);
    } else {
      setShowErrorModal(true);
      setIsSubmitting(false);
    }
  };

  const handleError = () => {
    setLoading(false);
    setError(true);
  };

  const handleZoomIn = () => {
    if (!loading) {
      const newScale = scale * 1.2;
      setScale(newScale);
    }
  };

  const handleZoomOut = () => {
    if (!loading) {
      const newScale = scale / 1.2;
      setScale(newScale);
    }
  };

  const getMobileButtonLabel = () => {
    if (!signingStarted) return "Start signing";
    if (!isAllChecked) return "Sign & Next";
    return "I Agree";
  };

  const handleMobilePrimaryAction = async () => {
    if (isSubmitting) return;

    // 1. START SIGNING
    if (!signingStarted) {
      setSigningStarted(true);

      if (flatSignCoordinates.length > 0) {
        setCurrentSignIndex(0);
        scrollToCoordinate(flatSignCoordinates[0]);
      }

      return;
    }

    // 2. SIGN & NEXT
    if (!isAllChecked) {
      const current = flatSignCoordinates[currentSignIndex];
      if (!current) return;

      const baseId =
        current.baseId ?? `${current.x}-${current.y}-${String(current.page)}`;

      const nextIndex = currentSignIndex + 1;
      const hasNext = nextIndex < flatSignCoordinates.length;

      if (hasNext) {
        scrollToCoordinate(flatSignCoordinates[nextIndex]);
      }

      const img = document.getElementById(`sign-img-${baseId}`);
      const timestamp = document.getElementById(`timestamp-${baseId}`);
      const highlight = document.getElementById(`highlight-${baseId}`);

      if (img) img.style.display = "block";
      if (timestamp) timestamp.style.display = "block";
      if (highlight) highlight.style.display = "none";

      setSignedCoordinates((prev) => [...prev, `${baseId}-signed`]);

      if (hasNext) {
        setCurrentSignIndex(nextIndex);
      } else {
        setCurrentSignIndex(null);
        setIsAllChecked(true);
        //scrolls to the end of the PDF
        const wrapper = wrapperRef.current;
        if (wrapper) {
          wrapper.scrollTop = wrapper.scrollHeight;
        }
      }

      return;
    }

    // 3. FINAL SUBMIT
    await onPdfClose();
  };

  const scrollToCoordinate = (coord) => {
    const { x, y, page, baseId: explicitBaseId } = coord;
    const baseId = explicitBaseId ?? `${x}-${y}-${String(page)}`;
    const pageNum = Number(page);

    const run = () => {
      const wrapper = wrapperRef.current;
      const anchor = document.getElementById(`pdf-scroll-anchor-${baseId}`);
      const scrollTarget =
        anchor ?? document.getElementById(`pdfContainer${pageNum}`);

      if (!scrollTarget) {
        console.warn("scroll target not found", scrollTarget);
        return;
      }

      if (!wrapper) {
        console.warn("wrapper not found", wrapper);
        scrollTarget.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
        return;
      }

      const wrapRect = wrapper.getBoundingClientRect();
      const targetRect = scrollTarget.getBoundingClientRect();
      const offsetTop = targetRect.top - wrapRect.top + wrapper.scrollTop;
      const nextTop = offsetTop - wrapRect.height / 2 + targetRect.height / 2;
      const maxScroll = Math.max(
        0,
        wrapper.scrollHeight - wrapper.clientHeight,
      );
      const top = Math.max(0, Math.min(nextTop, maxScroll));
      wrapper.scrollTo({ top, behavior: "smooth" });
    };

    requestAnimationFrame(() => requestAnimationFrame(run));
  };

  return (
    <>
      <Modal
        isOpen={Boolean(pdf)}
        onAfterOpen={() => setModalReady(true)}
        onRequestClose={() => {
          setModalReady(false);
          setViewedPdfIndex((prevIndex) => prevIndex + 1);
          localStorage.removeItem("scrolled");
          setPdf(null);
        }}
        className="digitalpdfModal"
      >
        <header className="digitalpdfmodalHeader">
          <div className="pdf-header-container">
            <h5>{pdf?.name}</h5>
            <div className="pdf-zoom-container">
              <div className="pdf-zoom-icon" onClick={handleZoomIn}>
                <svg
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeWidth="2"
                    d="m21 21-3.5-3.5M10 7v6m-3-3h6m4 0a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
                  />
                </svg>
              </div>
              {scale > calculateScale() && (
                <div className="pdf-zoom-icon" onClick={handleZoomOut}>
                  <svg
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeWidth="2"
                      d="m21 21-3.5-3.5M7 10h6m4 0a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
                    />
                  </svg>
                </div>
              )}
            </div>
          </div>
          <div className="close-pdf-modal">
            <ButtonIcon
              onClick={() => setViewedPdfIndex((prevIndex) => prevIndex + 1)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M4.28033 3.21967C3.98744 2.92678 3.51256 2.92678 3.21967 3.21967C2.92678 3.51256 2.92678 3.98744 3.21967 4.28033L6.93934 8L3.21967 11.7197C2.92678 12.0126 2.92678 12.4874 3.21967 12.7803C3.51256 13.0732 3.98744 13.0732 4.28033 12.7803L8 9.06066L11.7197 12.7803C12.0126 13.0732 12.4874 13.0732 12.7803 12.7803C13.0732 12.4874 13.0732 12.0126 12.7803 11.7197L9.06066 8L12.7803 4.28033C13.0732 3.98744 13.0732 3.51256 12.7803 3.21967C12.4874 2.92678 12.0126 2.92678 11.7197 3.21967L8 6.93934L4.28033 3.21967Z"
                  fill="#737278"
                />
              </svg>
            </ButtonIcon>
          </div>
        </header>
        <div ref={wrapperRef} className="canvasWrapper" onScroll={handleScroll}>
          <div
            id="canvas"
            ref={canvasRef}
            style={{ position: "relative", opacity: isSubmitting ? 0.3 : 1 }}
          />
          {loading && (
            <div className="viewpdfloader">
              <Spinner />
            </div>
          )}
          {error && (
            <div className="viewpdfloader">
              {" "}
              <p>Please contact your sales rep.</p>{" "}
            </div>
          )}
        </div>
        {!Boolean(pdf?.submittedOn) && !pdf?.isESigned && (
          <footer
            className={
              isEnd && isAllChecked
                ? "pdfreaderfooter"
                : "pdfreaderfooterdisabled"
            }
          >
            <div className="mobile-footer-container">
              <Button
                type="button"
                variant="secondary"
                size="large"
                onClick={() => setViewedPdfIndex((prevIndex) => prevIndex + 1)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                size="large"
                // disabled={!isEnd || !isAllChecked}
                // onClick={() => onPdfClose()}
                onClick={handleMobilePrimaryAction}
                isLoading={isSubmitting}
              >
                {getMobileButtonLabel()}
              </Button>
            </div>

            <div className="desktop-footer-container">
              <Button
                type="button"
                variant="primary"
                disabled={!isEnd || !isAllChecked}
                onClick={() => onPdfClose()}
                isLoading={isSubmitting}
              >
                I Agree
              </Button>
            </div>
          </footer>
        )}
      </Modal>

      <CustomModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        header="Error"
        headingCentered
        headingCenteredSpaced
        size="sm"
        customClass="digitalpdf-error-modal-overlay"
      >
        <p>An error occurred while submitting the PDF. Please try again.</p>
        <div className="digitalpdf-error-modal__footer">
          <Button
            type="button"
            variant="primary"
            onClick={() => setShowErrorModal(false)}
          >
            Close
          </Button>
        </div>
      </CustomModal>
    </>
  );
};

export default ViewPdfModal;
